import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Loader2, ClipboardList, CalendarClock, Users } from "lucide-react";
import client from "@/api/client";
import { toast } from "sonner";

const VIEWS = ["month", "week", "agenda"];
const TYPE_META = {
  task: { icon: ClipboardList, dot: "bg-primary", chip: "border-primary/30 bg-primary/10 text-primary" },
  booking: { icon: CalendarClock, dot: "bg-emerald-500", chip: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700" },
  event: { icon: Users, dot: "bg-amber-500", chip: "border-amber-500/30 bg-amber-500/10 text-amber-700" },
};

const iso = (d) => d.toISOString().slice(0, 10);
const startOfWeek = (d) => { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const addMonths = (d, n) => { const x = new Date(d); x.setDate(1); x.setMonth(x.getMonth() + n); return x; };

function timeOf(item, lang) {
  if (item.type === "task" || !item.start) return "";
  try {
    return new Date(item.start).toLocaleTimeString(lang || undefined, { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function labelOf(item, t) {
  if (item.type === "booking" && item.counterpart_name) {
    return t("memberCalendar.sessionWith", { defaultValue: "Session with {{name}}", name: item.counterpart_name });
  }
  return item.title;
}

function ItemChip({ item, onOpen, lang, t }) {
  const meta = TYPE_META[item.type] || TYPE_META.task;
  const Icon = meta.icon;
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className={`w-full text-start truncate rounded-md border px-1.5 py-0.5 text-[10px] font-medium transition-transform hover:scale-[1.02] ${meta.chip}`}
      title={labelOf(item, t)}
      data-testid={`calendar-item-${item.id}`}
    >
      <Icon className="inline h-2.5 w-2.5 me-1" />
      {timeOf(item, lang) && <span className="font-mono me-1">{timeOf(item, lang)}</span>}
      {labelOf(item, t)}
    </button>
  );
}

export function DashboardCalendarTab() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(() => new Date());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const range = useMemo(() => {
    if (view === "week") {
      const s = startOfWeek(cursor);
      return { start: iso(s), end: iso(addDays(s, 6)) };
    }
    if (view === "agenda") {
      return { start: iso(new Date()), end: iso(addDays(new Date(), 60)) };
    }
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    return { start: iso(gridStart), end: iso(addDays(gridStart, 41)) };
  }, [view, cursor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get(`/dashboard/calendar?start=${range.start}&end=${range.end}`);
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [range.start, range.end, t]);
  useEffect(() => { load(); }, [load]);

  const byDay = useMemo(() => {
    const map = {};
    items.forEach((i) => {
      const key = (i.start || "").slice(0, 10);
      if (!key) return;
      (map[key] = map[key] || []).push(i);
    });
    return map;
  }, [items]);

  const openItem = (item) => { if (item.link) navigate(item.link); };

  const shift = (dir) => {
    if (view === "week") setCursor((c) => addDays(c, dir * 7));
    else setCursor((c) => addMonths(c, dir));
  };

  const label = useMemo(() => {
    if (view === "agenda") return t("memberCalendar.next60", { defaultValue: "Next 60 days" });
    if (view === "week") {
      const s = startOfWeek(cursor);
      return `${s.toLocaleDateString(i18n.language, { month: "short", day: "numeric" })} – ${addDays(s, 6).toLocaleDateString(i18n.language, { month: "short", day: "numeric" })}`;
    }
    return cursor.toLocaleDateString(i18n.language, { month: "long", year: "numeric" });
  }, [view, cursor, i18n.language, t]);

  const todayKey = iso(new Date());
  const gridStart = useMemo(() => {
    if (view === "week") return startOfWeek(cursor);
    return startOfWeek(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
  }, [view, cursor]);
  const cellCount = view === "week" ? 7 : 42;
  const weekdays = useMemo(() => Array.from({ length: 7 }).map((_, i) =>
    addDays(startOfWeek(new Date()), i).toLocaleDateString(i18n.language, { weekday: "short" })
  ), [i18n.language]);

  const agendaGroups = useMemo(() => {
    const keys = Object.keys(byDay).filter((k) => k >= todayKey).sort();
    return keys.map((k) => ({ key: k, items: byDay[k] }));
  }, [byDay, todayKey]);

  return (
    <div className="space-y-4" data-testid="dashboard-calendar">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="rounded-xl h-9 w-9" onClick={() => shift(-1)} disabled={view === "agenda"} aria-label="Previous" data-testid="calendar-prev">
            <ChevronLeft className="h-4 w-4 rtl-flip" />
          </Button>
          <Button variant="outline" size="icon" className="rounded-xl h-9 w-9" onClick={() => shift(1)} disabled={view === "agenda"} aria-label="Next" data-testid="calendar-next">
            <ChevronRight className="h-4 w-4 rtl-flip" />
          </Button>
          <Button variant="ghost" size="sm" className="rounded-xl text-xs" onClick={() => setCursor(new Date())} data-testid="calendar-today">
            {t("memberCalendar.today", { defaultValue: "Today" })}
          </Button>
        </div>
        <h3 className="font-display text-base md:text-lg font-semibold" data-testid="calendar-label">{label}</h3>
        <div className="ms-auto flex gap-1.5">
          {VIEWS.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-all ${
                view === v ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:border-primary/40 hover:text-primary"
              }`}
              data-testid={`calendar-view-${v}`}
            >
              {t(`memberCalendar.view.${v}`, { defaultValue: v })}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {Object.keys(TYPE_META).map((k) => (
          <span key={k} className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span className={`h-2 w-2 rounded-full ${TYPE_META[k].dot}`} />
            {t(`memberCalendar.type.${k}`, { defaultValue: k })}
          </span>
        ))}
        <Badge variant="outline" className="rounded-full text-[10px] ms-auto" data-testid="calendar-count">
          {items.length} {t("memberCalendar.entries", { defaultValue: "entries" })}
        </Badge>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : view === "agenda" ? (
        agendaGroups.length === 0 ? (
          <Card className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="calendar-agenda-empty">
            <p className="text-sm text-muted-foreground">{t("memberCalendar.empty", { defaultValue: "Nothing scheduled yet." })}</p>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="calendar-agenda">
            {agendaGroups.map((g) => (
              <Card key={g.key} className="rounded-2xl border border-border p-4">
                <div className="flex items-center gap-2 mb-2">
                  <p className="font-display text-sm font-semibold">
                    {new Date(`${g.key}T00:00:00`).toLocaleDateString(i18n.language, { weekday: "long", month: "short", day: "numeric" })}
                  </p>
                  {g.key === todayKey && (
                    <Badge className="rounded-full bg-primary/12 text-primary border-primary/30 text-[10px]">
                      {t("memberCalendar.today", { defaultValue: "Today" })}
                    </Badge>
                  )}
                  <span className="ms-auto font-mono text-[11px] text-muted-foreground">{g.items.length}</span>
                </div>
                <ul className="space-y-1.5">
                  {g.items.map((i) => {
                    const meta = TYPE_META[i.type] || TYPE_META.task;
                    const Icon = meta.icon;
                    return (
                      <li key={`${i.type}-${i.id}`}>
                        <button
                          type="button"
                          onClick={() => openItem(i)}
                          className="w-full text-start flex items-center gap-3 rounded-xl border border-border p-2.5 hover:border-primary/40 hover:shadow-sm transition-all"
                          data-testid={`calendar-agenda-item-${i.id}`}
                        >
                          <span className={`flex h-8 w-8 items-center justify-center rounded-lg border ${meta.chip}`}>
                            <Icon className="h-4 w-4" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium truncate">{labelOf(i, t)}</span>
                            <span className="block text-[11px] text-muted-foreground">
                              {t(`memberCalendar.type.${i.type}`, { defaultValue: i.type })}
                              {timeOf(i, i18n.language) ? ` · ${timeOf(i, i18n.language)}` : ""}
                              {i.status ? ` · ${i.status}` : ""}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </Card>
            ))}
          </div>
        )
      ) : (
        <Card className="rounded-2xl border border-border overflow-hidden" data-testid={`calendar-grid-${view}`}>
          <div className="grid grid-cols-7 border-b border-border bg-muted/30">
            {weekdays.map((w) => (
              <div key={w} className="px-2 py-2 text-[10px] uppercase tracking-widest text-muted-foreground text-center truncate">{w}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {Array.from({ length: cellCount }).map((_, idx) => {
              const day = addDays(gridStart, idx);
              const key = iso(day);
              const inMonth = view === "week" || day.getMonth() === cursor.getMonth();
              const dayItems = byDay[key] || [];
              return (
                <div
                  key={key}
                  className={`min-h-[92px] ${view === "week" ? "sm:min-h-[220px]" : ""} border-b border-e border-border p-1.5 space-y-1 ${inMonth ? "" : "bg-muted/20 opacity-60"}`}
                  data-testid={`calendar-day-${key}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`font-mono text-[11px] ${key === todayKey ? "flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold" : "text-muted-foreground"}`}>
                      {day.getDate()}
                    </span>
                    {dayItems.length > 2 && view === "month" && (
                      <span className="text-[9px] font-mono text-muted-foreground">{dayItems.length}</span>
                    )}
                  </div>
                  {/* Phones: titles are unreadable in a 45px cell, so show
                      coloured dots and let the agenda view carry the detail. */}
                  {dayItems.length > 0 && (
                    <button
                      type="button"
                      onClick={() => { setView("agenda"); }}
                      className="sm:hidden flex flex-wrap items-center gap-0.5 w-full"
                      aria-label={`${dayItems.length} entries`}
                      data-testid={`calendar-day-dots-${key}`}
                    >
                      {dayItems.slice(0, 4).map((i) => (
                        <span key={`${i.type}-${i.id}`} className={`h-1.5 w-1.5 rounded-full ${(TYPE_META[i.type] || TYPE_META.task).dot}`} />
                      ))}
                      {dayItems.length > 4 && <span className="text-[8px] text-muted-foreground">+{dayItems.length - 4}</span>}
                    </button>
                  )}
                  <div className="hidden sm:block space-y-1">
                    {(view === "week" ? dayItems : dayItems.slice(0, 3)).map((i) => (
                      <ItemChip key={`${i.type}-${i.id}`} item={i} onOpen={openItem} lang={i18n.language} t={t} />
                    ))}
                    {view === "month" && dayItems.length > 3 && (
                      <p className="text-[9px] text-muted-foreground ps-1">+{dayItems.length - 3}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

export default DashboardCalendarTab;
